import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { CaseContact } from '../../../../models/case-contact.model';
import { CaseContactService } from '../../../../services/case-contact.service';
import { UserConfigs } from '../../../../models/user-configs.model';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { PatchDeltaObjectUtility } from '../../../../utilities/patch-delta-object.utility';
import { ExportSdk } from '../../../../sdks/export.sdk';
import { ImportSdk } from '../../../../sdks/import.sdk';

@Component({
    selector: 'lex-case-contact-edit',
    templateUrl: './case-contact-edit.component.html',
    styleUrls: ['./case-contact-edit.component.scss'],
    standalone: false
})
export class CaseContactEditComponent implements OnInit, OnChanges, OnDestroy {

	@Input() contact: CaseContact;
	@Input() showSelect: boolean;
	@Input() canEdit: boolean;
	@Output() getContactsEvent = new EventEmitter();

	@ViewChild('fileUpload', { static: false }) fileInputElement: ElementRef;

	caseContactForm: UntypedFormGroup;

	private subscription = new Subscription();

	affiliations: string[];
	filteredAffiliations: Observable<string[]>;

	constructor(private formBuilder: UntypedFormBuilder, private contactService: CaseContactService, private snackBar: MatSnackBar, private userService: UserConfigsService, private exportSdk: ExportSdk, private importSdk: ImportSdk) { }

	ngOnInit(): void {
		if (this.contact) {
			this.onChangeContact();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && this.contact) {
			this.onChangeContact();
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onChangeContact(): void {
		this.caseContactForm = this.formBuilder.group({
			name: [this.contact.name, [Validators.required, Validators.maxLength(256)]],
			isOrganization: [{ value: this.contact.isOrganization, disabled: !this.canEdit }, Validators.required],
			title: [this.contact.title, [Validators.maxLength(50)]],
			memo: [this.contact.memo, Validators.maxLength(1024)],
			address: [this.contact.address, Validators.maxLength(256)],
			phone: [this.contact.phone, [Validators.maxLength(50)]],
			fax: [this.contact.fax, [Validators.maxLength(50)]],
			cell: [this.contact.cell, [Validators.maxLength(50)]],
			email: [this.contact.email, [Validators.email, Validators.maxLength(50)]],
			affiliation: [this.contact.affiliation]
		});

		//get affiliation list needed by Affiliation autocomplete input field
		this.getAffiliationList();
	}

	onExportContacts(): void {
		this.exportSdk.exportContacts().subscribe((url) => {
			window.open(url, '_blank');
		});	
	}

	onFileSelected(event): void {
		const file: File = event.target.files[0];
		if (file) {

			this.subscription.add(
				this.userService.getUserConfigs().pipe(
					switchMap((userConfig: UserConfigs) => {
						const formData = new FormData();
						formData.append('File', file, file.name);
						formData.append('FileType', file.name.substr(file.name.lastIndexOf('.')));
						formData.append('AccountId', userConfig.currentCase.accountId);

						return this.importSdk.importContacts(formData);
					}))
					.subscribe(() => {
						this.fileInputElement.nativeElement.value = '';
						if (this.contact)
							this.getContactsEvent.emit(this.contact.contactId);
						else
							this.getContactsEvent.emit();
						this.snackBar.open('Contacts imported successfully.', 'X', {
							duration: 3000
						});
					},
						() => {
							this.fileInputElement.nativeElement.value = '';
							this.snackBar.open('Import Contacts encountered an error.', 'X', {
								duration: 3000
							});
						})
			);
		}
		else {
			this.fileInputElement.nativeElement.value = '';
		}
	}

	onUpdateContact(): void {
		if (!this.caseContactForm.valid)
			return;

		const jobPatch = PatchDeltaObjectUtility.getDeltaObject<CaseContact>(this.caseContactForm.controls);

		if (jobPatch === null)
			return;

		this.contactService.updateContact(this.contact.contactId, jobPatch).subscribe(() => {
			const keyValueArray = Object.entries(jobPatch);
			keyValueArray.forEach(kvp => {
				this.contact[kvp[0]] = kvp[1];
			});
			this.onChangeContact();
			this.snackBar.open('Contact updated successfully.', 'X', {
				duration: 3000
			});
		});
	}

	onUndo(): void {
		this.onChangeContact();
	}

	private filter(value: string): string[] {
		const filterValue = value.toLowerCase();
		return this.affiliations.filter(option => option.toLowerCase().includes(filterValue));
	}

	private getAffiliationList(): void {
		if (this.contact) {
			this.contactService.getContactAffiliation().subscribe(data => {
				this.affiliations = data;
				this.filteredAffiliations = this.caseContactForm.get('affiliation').valueChanges
					.pipe(
						startWith(''),
						map(value => this.filter(value))
					);
			});
		}
	}
}
